'use client';

import { useState, useEffect } from 'react';
import { Copy, Check, AlertCircle, Upload, Download } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { pythonFormatterContent } from '../../data/python-formatter-content';
import Link from 'next/link';

export default function PythonFormatter() {
    const [input, setInput] = useState('');
    const [output, setOutput] = useState('');
    const [error, setError] = useState('');
    const [copied, setCopied] = useState(false);
    const [indent, setIndent] = useState(4);
    const [useTabs, setUseTabs] = useState(false);

    useEffect(() => {
        // Load any saved input from localStorage
        const savedInput = localStorage.getItem('pythonFormatterInput');
        if (savedInput) {
            setInput(savedInput);
        }
    }, []);

    const formatPython = () => {
        try {
            if (!input.trim()) {
                setError('Please enter some Python code to format');
                return;
            }

            // Simple Python formatting - for more complex formatting, consider a server-side solution
            let formatted = input
                .replace(/\r\n/g, '\n')  // Normalize line endings
                .split('\n')
                .map(line => line.trim() === '' ? '' : line)
                .join('\n');

            // Basic indentation
            let currentIndent = 0;
            const lines = formatted.split('\n');
            const formattedLines: string[] = [];
            const stack: string[] = [];

            for (const line of lines) {
                const trimmed = line.trim();
                if (!trimmed) {
                    formattedLines.push('');
                    continue;
                }

                // Handle dedent
                while (stack.length > 0 &&
                    (trimmed === stack[stack.length - 1] ||
                        !line.startsWith(' '.repeat(currentIndent * indent) + stack[stack.length - 1]))) {
                    stack.pop();
                    currentIndent--;
                }

                // Add indentation
                const indentation = useTabs
                    ? '\t'.repeat(currentIndent)
                    : ' '.repeat(currentIndent * indent);

                formattedLines.push(indentation + trimmed);

                // Handle indent for next line
                if (trimmed.endsWith(':')) {
                    stack.push(trimmed);
                    currentIndent++;
                }
            }

            const result = formattedLines.join('\n');
            setOutput(result);
            setError('');

            // Save to localStorage
            localStorage.setItem('pythonFormatterInput', input);
            toast.success('Python code formatted successfully!');
        } catch (err) {
            setError('Error formatting Python: ' + (err as Error).message);
            setOutput('');
        }
    };

    const copyToClipboard = () => {
        if (!output) return;

        navigator.clipboard.writeText(output).then(() => {
            setCopied(true);
            toast.success('Copied to clipboard!');
            setTimeout(() => setCopied(false), 2000);
        });
    };

    const clearAll = () => {
        setInput('');
        setOutput('');
        setError('');
        localStorage.removeItem('pythonFormatterInput');
    };

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (event) => {
            const content = event.target?.result as string;
            setInput(content);
        };
        reader.readAsText(file);
    };

    const downloadFile = () => {
        if (!output) return;
        const blob = new Blob([output], { type: 'text/python;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'formatted.py';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const loadSample = () => {
        const sampleCode = `# Sample Python code for testing the formatter

def greet(name):
    # This is a simple function
    return f"Hello, {name}!"

class SampleClass:
    def __init__(self, value):
        self.value = value
    
    def display(self):
        if self.value > 10:
            print("Value is greater than 10")
        else:
            print("Value is 10 or less")

# List comprehension
numbers = [1, 2, 3, 4, 5]
squares = [x**2 for x in numbers]

# Dictionary comprehension
square_dict = {x: x**2 for x in range(5)}

# Try-except block
try:
    result = 10 / 0
except ZeroDivisionError as e:
    print(f"Error: {e}")

# Using the sample code
if __name__ == "__main__":
    print(greet("Python Developer"))
    sample = SampleClass(15)
    sample.display()`;

        setInput(sampleCode);
        toast.success('Sample Python code loaded!');
    };

    return (
        <div className="max-w-6xl mx-auto p-4 md:p-6">
            <div className="mb-8">
                <ToolPageIntro title="Python Formatter">
                Format and beautify Python code with our free online tool. Clean up messy Python scripts and improve code readability with proper indentation.
            </ToolPageIntro>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                <div className="space-y-2">
                    <div className="flex justify-between items-center">
                        <label htmlFor="input" className="block text-sm font-medium text-gray-700">
                            Input Python
                        </label>
                        <div className="flex space-x-2">
                            <button
                                onClick={loadSample}
                                className="px-3 py-1.5 bg-blue-50 text-blue-700 text-sm rounded hover:bg-blue-100 transition-colors"
                            >
                                Load Sample
                            </button>
                            <label className="cursor-pointer px-3 py-1.5 bg-gray-100 text-gray-700 text-sm rounded hover:bg-gray-200 transition-colors flex items-center">
                                <Upload className="h-4 w-4 mr-1" />
                                Upload
                                <input
                                    type="file"
                                    accept=".py,.pyw"
                                    className="hidden"
                                    onChange={handleFileUpload}
                                />
                            </label>
                            <button
                                onClick={clearAll}
                                className="px-3 py-1.5 bg-gray-200 text-gray-700 text-sm rounded hover:bg-gray-300 transition-colors"
                            >
                                Clear All
                            </button>
                        </div>
                    </div>
                    <div className="relative">
                        <textarea
                            id="input"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            className="w-full h-96 font-mono text-sm p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder='# Paste your Python code here...
def hello():
    print("Hello, World!")'
                            spellCheck="false"
                        />
                    </div>
                </div>

                <div className="space-y-2">
                    <div className="flex justify-between items-center">
                        <label htmlFor="output" className="block text-sm font-medium text-gray-700">
                            Formatted Python
                        </label>
                        <div className="flex items-center space-x-2">
                            <div className="flex items-center">
                                <label htmlFor="indent" className="text-xs text-gray-500 mr-2">
                                    Indent:
                                </label>
                                <select
                                    id="indent"
                                    value={indent}
                                    onChange={(e) => setIndent(Number(e.target.value))}
                                    className="text-sm border border-gray-300 rounded px-2 py-1"
                                >
                                    <option value={2}>2 spaces</option>
                                    <option value={4}>4 spaces (PEP 8)</option>
                                    <option value={8}>8 spaces</option>
                                </select>
                            </div>
                            <div className="flex items-center ml-2">
                                <label className="inline-flex items-center">
                                    <input
                                        type="checkbox"
                                        checked={useTabs}
                                        onChange={(e) => setUseTabs(e.target.checked)}
                                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                    />
                                    <span className="ml-2 text-xs text-gray-700">Use tabs</span>
                                </label>
                            </div>
                            <button
                                onClick={copyToClipboard}
                                disabled={!output}
                                className={`p-1.5 rounded ${output
                                    ? 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                                    : 'text-gray-300 cursor-not-allowed'
                                    }`}
                                title="Copy to clipboard"
                            >
                                {copied ? (
                                    <Check className="h-4 w-4 text-green-500" />
                                ) : (
                                    <Copy className="h-4 w-4" />
                                )}
                            </button>
                            <button
                                onClick={downloadFile}
                                disabled={!output}
                                className={`p-1.5 rounded ${output
                                    ? 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                                    : 'text-gray-300 cursor-not-allowed'
                                    }`}
                                title="Download file"
                            >
                                <Download className="h-4 w-4" />
                            </button>
                        </div>
                    </div>
                    <div className="relative">
                        <pre className="h-96 overflow-auto p-3 bg-gray-50 border border-gray-300 rounded-lg text-sm font-mono">
                            <code className="text-gray-800">
                                {output || (error ? '' : 'Formatted Python will appear here...')}
                            </code>
                        </pre>
                        {error && (
                            <div className="absolute bottom-2 left-2 right-2 p-2 bg-red-50 border border-red-200 rounded text-red-600 text-sm flex items-start">
                                <AlertCircle className="h-4 w-4 mr-1.5 mt-0.5 flex-shrink-0" />
                                <span>{error}</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="flex justify-center mb-8">
                <button
                    onClick={formatPython}
                    disabled={!input.trim()}
                    className={`px-6 py-2 rounded-md text-white font-medium ${input.trim()
                        ? 'bg-blue-600 hover:bg-blue-700'
                        : 'bg-gray-400 cursor-not-allowed'
                        } transition-colors`}
                >
                    Format Python
                </button>
            </div>

            <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                <h3 className="font-medium text-blue-800 mb-2">How to use the Python Formatter</h3>
                <ol className="list-decimal list-inside space-y-1 text-sm text-blue-700">
                    <li>Paste your Python code in the left panel, upload a .py file, or click "Load Sample"</li>
                    <li>Click the "Format Python" button to format the code</li>
                    <li>Use the indentation options to adjust the output format (PEP 8 recommends 4 spaces)</li>
                    <li>Copy or download the formatted Python code</li>
                </ol>
            </div>

            {/* Informational Content */}
            <InformationalContent
                title={pythonFormatterContent.title}
                description={pythonFormatterContent.description}
                content={pythonFormatterContent.content}
                faqs={pythonFormatterContent.faqs}
                relatedTools={pythonFormatterContent.relatedTools}
            />
        </div>
    );
}
